/**
 * TreeNodeEditor — right-pane settings editor for a node in the
 * Visual Builder's nested tree schema. Renders different fields per
 * node type:
 *   • Section: background, padding, radius, max width.
 *   • Row:     gap, alignment, wrap.
 *   • Column:  span (1..12), padding, background.
 *   • Widget:  comp picker + safe UI settings for the current comp.
 *
 * All writes go through `updateNode` / `updateNodeSettings` from the
 * treeSchema helpers — no business logic is ever touched.
 */
import { X, Square, Rows, Columns, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { updateNode, updateNodeSettings, findNode } from "@/lib/visualBuilder/treeSchema";

const NODE_META = {
  section: { title: "Section",  icon: Square,  color: "#E32636" },
  row:     { title: "Row",      icon: Rows,    color: "#0284c7" },
  col:     { title: "Column",   icon: Columns, color: "#059669" },
  widget:  { title: "Widget",   icon: Package, color: "#475569" },
};

const WIDGET_COMPS = [
  { value: "text",         label: "Text" },
  { value: "image",        label: "Image" },
  { value: "button",       label: "Button" },
  { value: "hero",         label: "Hero Banner" },
  { value: "deal_banner",  label: "Deal Banner" },
  { value: "divider",      label: "Divider" },
  { value: "spacer",       label: "Spacer" },
];

export default function TreeNodeEditor({ tree, nodeId, onChange, onClose }) {
  const hit = findNode(tree || [], nodeId);
  if (!hit) {
    return (
      <div className="p-8 text-center text-slate-400 text-[12px]">
        This node no longer exists.
        <div className="mt-2">
          <button type="button" onClick={onClose} className="text-[11px] font-semibold text-[#E32636]">Close</button>
        </div>
      </div>
    );
  }
  const node = hit.node;
  const meta = NODE_META[node.type] || NODE_META.widget;
  const Icon = meta.icon;
  const s = node.settings || {};

  const patchNode = (patch) => onChange({ tree: updateNode(tree, node.id, patch) });
  const patchSettings = (patch) => onChange({ tree: updateNodeSettings(tree, node.id, patch) });

  return (
    <div className="p-4 space-y-4" data-testid={`vb-tree-editor-${node.id}`}>
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: meta.color, color: "#fff" }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Tree · {meta.title}</div>
          <div className="text-[15px] font-semibold text-slate-900 truncate">{node.label || node.type}</div>
          <div className="text-[10.5px] font-mono text-slate-400 truncate">{node.id}</div>
        </div>
        <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid="vb-tree-editor-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Label */}
      <Field label="Layer Label">
        <Input value={node.label ?? ""} onChange={(e) => patchNode({ label: e.target.value })} className="h-8 text-[12px]" data-testid="vb-tree-node-label" />
      </Field>

      {node.type === "section" && (
        <>
          <Field label="Background"><ColorInput value={s.bg} onChange={(v) => patchSettings({ bg: v })} testid="vb-tree-section-bg" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <NumberField label="Padding X" value={s.padding_x} defaultVal={24} onChange={(v) => patchSettings({ padding_x: v })} testid="vb-tree-section-px" />
            <NumberField label="Padding Y" value={s.padding_y} defaultVal={32} onChange={(v) => patchSettings({ padding_y: v })} testid="vb-tree-section-py" />
            <NumberField label="Radius"    value={s.radius} defaultVal={0}  onChange={(v) => patchSettings({ radius: v })} testid="vb-tree-section-radius" />
            <Field label="Max Width"><Input value={s.max_width ?? ""} onChange={(e) => patchSettings({ max_width: e.target.value })} placeholder="e.g. 1200px" className="h-8 text-[12px]" data-testid="vb-tree-section-max" /></Field>
          </div>
        </>
      )}

      {node.type === "row" && (
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Gap"  value={s.gap} defaultVal={16} onChange={(v) => patchSettings({ gap: v })} testid="vb-tree-row-gap" />
          <SelectField label="Align" value={s.align || "stretch"} onChange={(v) => patchSettings({ align: v })} testid="vb-tree-row-align"
            options={[{value:"start",label:"Top"},{value:"center",label:"Center"},{value:"end",label:"Bottom"},{value:"stretch",label:"Stretch"}]} />
          <SelectField label="Wrap" value={s.wrap || "wrap"} onChange={(v) => patchSettings({ wrap: v })} testid="vb-tree-row-wrap"
            options={[{value:"wrap",label:"Wrap"},{value:"nowrap",label:"Nowrap"}]} />
        </div>
      )}

      {node.type === "col" && (
        <div className="space-y-3">
          <Field label={`Span (${node.span || 12}/12)`}>
            <input
              type="range" min={1} max={12} step={1}
              value={node.span || 12}
              onChange={(e) => patchNode({ span: Number(e.target.value) })}
              className="w-full accent-[#E32636]"
              data-testid="vb-tree-col-span"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <NumberField label="Padding" value={s.padding} defaultVal={8} onChange={(v) => patchSettings({ padding: v })} testid="vb-tree-col-padding" />
            <Field label="Background"><ColorInput value={s.bg} onChange={(v) => patchSettings({ bg: v })} testid="vb-tree-col-bg" /></Field>
          </div>
        </div>
      )}

      {node.type === "widget" && (
        <>
          <SelectField
            label="Widget Type"
            value={node.comp || "text"}
            onChange={(v) => patchNode({ comp: v })}
            testid="vb-tree-widget-comp"
            options={WIDGET_COMPS}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Content / Label" full>
              <Input value={s.content ?? ""} onChange={(e) => patchSettings({ content: e.target.value })} placeholder="Text / button label / hero subtitle" className="h-8 text-[12px]" data-testid="vb-tree-widget-content" />
            </Field>
            {["hero", "deal_banner"].includes(node.comp) && (
              <Field label="Title" full>
                <Input value={s.title ?? ""} onChange={(e) => patchSettings({ title: e.target.value })} className="h-8 text-[12px]" data-testid="vb-tree-widget-title" />
              </Field>
            )}
            {["image", "hero", "deal_banner"].includes(node.comp) && (
              <Field label="Image URL" full>
                <Input value={s.image_url ?? ""} onChange={(e) => patchSettings({ image_url: e.target.value })} className="h-8 text-[12px]" data-testid="vb-tree-widget-image" placeholder="https://…" />
              </Field>
            )}
            {node.comp === "button" && (
              <Field label="Link" full>
                <Input value={s.href ?? ""} onChange={(e) => patchSettings({ href: e.target.value })} className="h-8 text-[12px]" data-testid="vb-tree-widget-href" placeholder="/shop or https://…" />
              </Field>
            )}
            <Field label="Background"><ColorInput value={s.bg} onChange={(v) => patchSettings({ bg: v })} testid="vb-tree-widget-bg" /></Field>
            <Field label="Text Color"><ColorInput value={s.text_color} onChange={(v) => patchSettings({ text_color: v })} testid="vb-tree-widget-text-color" /></Field>
            <NumberField label="Font Size" value={s.font_size} defaultVal={14} onChange={(v) => patchSettings({ font_size: v })} testid="vb-tree-widget-font-size" />
            <NumberField label="Radius"    value={s.radius}    defaultVal={0}  onChange={(v) => patchSettings({ radius: v })} testid="vb-tree-widget-radius" />
            <SelectField
              label="Align"
              value={s.align || "left"}
              onChange={(v) => patchSettings({ align: v })}
              testid="vb-tree-widget-align"
              options={[{value:"left",label:"Left"},{value:"center",label:"Center"},{value:"right",label:"Right"}]}
            />
            {node.comp === "spacer" && (
              <NumberField label="Height" value={s.height} defaultVal={24} onChange={(v) => patchSettings({ height: v })} testid="vb-tree-widget-height" />
            )}
            {node.comp === "divider" && (
              <NumberField label="Thickness" value={s.thickness} defaultVal={1} onChange={(v) => patchSettings({ thickness: v })} testid="vb-tree-widget-thickness" />
            )}
          </div>
        </>
      )}

      <div className="pt-2 text-[10.5px] text-slate-400 border-t border-slate-100 leading-relaxed">
        Layout &amp; presentation only. No pricing, stock, cart, or checkout logic is editable here.
      </div>
    </div>
  );
}

/* ─── Small field primitives ────────────────────────────────────── */

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</Label>
      {children}
    </div>
  );
}

function NumberField({ label, value, defaultVal, onChange, testid }) {
  return (
    <Field label={label}>
      <Input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
        placeholder={String(defaultVal ?? "")}
        className="h-8 text-[12px]"
        data-testid={testid}
      />
    </Field>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <Field label={label}>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-full text-[12px] rounded-md border border-slate-200 px-2 bg-white"
        data-testid={testid}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

function ColorInput({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <input type="color" value={value || "#ffffff"} onChange={(e) => onChange(e.target.value)} className="h-8 w-8 rounded-lg border border-slate-200 cursor-pointer" data-testid={`${testid}-picker`} />
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="auto" className="h-8 text-[11px] font-mono flex-1" data-testid={testid} />
    </div>
  );
}
